/**
 * NewBadge — "NEW" notched ribbon primitive.
 *
 * Figma source: `slider/tag` component `70:3047` (single component, not a set).
 * See design-system-manifest §5.4. 45×22 pixels (HUG).
 *
 * Geometry (all from manifest):
 *  - layoutMode=HORIZONTAL, counterAxisAlignItems=CENTER, itemSpacing=2 → gap-0.5
 *  - Padding: L=8 R=12 T=4 B=6 (asymmetric) → pl-2 pr-3 pt-1 pb-1.5
 *  - rectangleCornerRadii [0, 0, 0, 12] → inline-style borderRadius
 *    '0 0 0 12px' (top-left=0, top-right=0, bottom-right=0, bottom-left=12)
 *  - Fill: GRADIENT_LINEAR (0,0)→(1,1) with stops 552:6909 → 552:6796,
 *    same orange gradient as uiButton primary. Tailwind
 *    bg-gradient-to-br from-accent-orange-start to-accent-orange-end.
 *  - Effect: BACKGROUND_BLUR 16px → backdrop-blur-tag.
 *  - Text: "NEW", Raleway Bold 10/11.74 (text-badge), color text-inverse (#000).
 *
 * Positioning in cover: when `isNew=true` on a cover instance, the badge
 * has Figma constraints {vertical:TOP, horizontal:RIGHT} → place via
 * `className="absolute top-0 right-0"` on the consumer side. The primitive
 * itself is position-agnostic.
 *
 * Flush-to-cover hint (`coverRadius`): когда badge стоит в TR-углу cover-
 * элемента с `overflow-hidden` + `rounded-home-N`, Figma полагается на то,
 * что cover клипит TR-угол badge'а под свой radius. В DOM это даёт
 * визуальный «зазор» (см. дизайн-ревью Figma `363:4576` 2026-05-21).
 * Установив `coverRadius={N}` consumer передаёт собственный radius cover'а
 * — primitive выставит TR-угол badge'а равным этому значению, и badge
 * визуально «прильнёт» к скруглению cover'а вместо клипа. Default = 0
 * (точно по Figma primitive `70:3047`, для случаев без overflow-hidden).
 *
 * Props:
 *  - coverRadius? (number) — radius parent-cover в px, чтобы выставить TR
 *                            badge'а равным ему. Default 0.
 *  - className? passthrough (use for position: `absolute top-0 right-0`)
 */
export default function NewBadge({ coverRadius = 0, className = '', ...rest }) {
  const cls = [
    // Figma auto-layout: HORIZONTAL + CENTER + itemSpacing 2
    'inline-flex items-center',
    'gap-0.5',
    // Asymmetric padding (L=8 R=12 T=4 B=6)
    'pt-1 pr-3 pb-1.5 pl-2',
    // Orange gradient (same fill style as uiButton primary — styles.fills 123:2955)
    'bg-gradient-to-br from-accent-orange-start to-accent-orange-end',
    // Background blur 16
    'backdrop-blur-tag',
    // Typography — Raleway Bold 10/11.74 + black
    'font-sans font-bold text-badge text-text-inverse',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Asymmetric cornerRadius — Figma manifest §5.4 даёт [0,0,0,12] (TL/TR/BR/BL).
  // TR override через `coverRadius` нужен только если badge клипится parent-
  // cover'ом с overflow-hidden + rounded-N (DOM не клипит как Figma — см.
  // дизайн-ревью `363:4576` 2026-05-21). Default coverRadius=0 = строго Figma.
  const borderRadius = `0px ${coverRadius}px 0px 12px`;
  return (
    <span
      className={cls}
      style={{ borderRadius }}
      aria-label="NEW"
      data-testid="new-badge"
      {...rest}
    >
      NEW
    </span>
  );
}
